import { formatINR, formatQty, type Quantity } from '@project/contracts'

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

/**
 * A date-only value is parsed by hand: `new Date('2026-08-21')` is UTC
 * midnight, which renders as the 20th anywhere west of Greenwich.
 */
const toParts = (value: string): { d: number; m: number; y: number; time?: Date } | null => {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (dateOnly) {
    return { y: Number(dateOnly[1]), m: Number(dateOnly[2]) - 1, d: Number(dateOnly[3]) }
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return { y: parsed.getFullYear(), m: parsed.getMonth(), d: parsed.getDate(), time: parsed }
}

/** dd-MMM-yyyy, the format used on every printed document. */
export const formatDate = (value: unknown): string => {
  if (typeof value !== 'string' || value === '') return ''
  const parts = toParts(value)
  if (!parts) return value
  return `${String(parts.d).padStart(2, '0')}-${MONTHS[parts.m] ?? ''}-${parts.y}`
}

export const formatDateTime = (value: unknown): string => {
  if (typeof value !== 'string' || value === '') return ''
  const parts = toParts(value)
  if (!parts) return value
  const date = formatDate(value)
  if (!parts.time) return date
  const h24 = parts.time.getHours()
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12
  const mins = String(parts.time.getMinutes()).padStart(2, '0')
  return `${date}, ${h12}:${mins} ${h24 < 12 ? 'am' : 'pm'}`
}

/** Everything financial stays a string from the database to the screen. */
export const asDecimalString = (value: unknown): string | null => {
  if (typeof value === 'string' && /^-?\d+(\.\d+)?$/.test(value)) return value
  return null
}

export const formatMoney = (value: unknown): string => {
  const decimal = asDecimalString(value)
  return decimal === null ? '' : formatINR(decimal)
}

export const formatQuantity = (value: unknown): string => {
  const decimal = asDecimalString(value)
  return decimal === null ? '' : formatQty(decimal as Quantity)
}

/** Rates carry up to 4 places but are shown at the precision they were entered. */
export const formatRate = (value: unknown): string => {
  const decimal = asDecimalString(value)
  if (decimal === null) return ''
  const trimmed = decimal.includes('.') ? decimal.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '') : decimal
  return formatINR(trimmed, { symbol: false, dp: Math.max(2, (trimmed.split('.')[1] ?? '').length) })
}

export const asText = (value: unknown): string => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return ''
}

export const rangeLabel = (page: number, pageSize: number, total: number): string => {
  if (total === 0) return '0 of 0'
  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)
  return `${first}-${last} of ${total}`
}
